import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { highlightText } from './BinHighlightUtils.js';
import { sortResults } from '../../utils/binHelpers.js';

const PartSearchView = ({
  query,
  results,
  selectedIndex,
  selectedBolt,
  inputRef,
  resultsRef,
  sessionId,
  onQueryChange,
  onKeyDown,
  onBoltSelected,
  onClearSelection,
  mode,
  setMode,
  isSettingsOpen,
  setIsSettingsOpen,
}) => {
  const filteredResults = sortResults(results);
  const [showQr, setShowQr] = useState(false);

  // Use your real local IP for the QR code when running locally
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const qrUrl = isLocalhost
    ? `http://192.168.139.144:3000/remote/${sessionId}`
    : `${window.location.origin}/remote/${sessionId}`;

  return (
    <div
      className="fixed top-0 right-0 h-full w-80 bg-white shadow-lg z-40 overflow-y-auto flex flex-col"
      ref={resultsRef}
    >
      <div className="p-4 sticky top-0 bg-white z-10">
        <h2 className="text-2xl font-bold text-center mb-4 text-blue-800">Part Location Search</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search for a bolt (e.g., M3x8)"
            className="w-full px-4 py-2 pl-10 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
            value={query}
            ref={inputRef}
            onChange={onQueryChange}
            onKeyDown={onKeyDown}
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500"
            size={18}
          />
        </div>
        {selectedBolt && (
          <button
            onClick={onClearSelection}
            className="mt-3 w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            Part Found
          </button>
        )}
      </div>

      <div className="p-4 flex-1 overflow-y-auto">
        {filteredResults.length > 0 ? (
          <ul className="space-y-2">
            {filteredResults.map((bolt, index) => (
              <li
                key={index}
                className={`p-3 flex justify-between items-center rounded-md transition duration-200 ease-in-out
                  ${
                    index === selectedIndex
                      ? 'bg-blue-500 text-white shadow-md transform scale-105'
                      : 'bg-white hover:bg-blue-100 shadow-sm'
                  }`}
                onMouseDown={() => onBoltSelected(bolt)}
              >
                <span className="text-base font-medium">
                  {highlightText(bolt['Bolt Size'], query)}
                </span>
                <span
                  className={`text-xs ${
                    index === selectedIndex ? 'text-blue-100' : 'text-blue-500'
                  } font-semibold`}
                >
                  Bin {bolt['Bin Number']}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          query.length > 0 && (
            <div className="text-center text-gray-500 mt-4">
              No results found for "{query}"
            </div>
          )
        )}
      </div>

      <div className="mt-auto p-4 border-t">
        <button
          onClick={() => setShowQr(!showQr)}
          className="w-full px-4 py-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 font-semibold"
        >
          {showQr ? 'Close' : 'Search from Mobile App'}
        </button>

        {showQr && (
          <div className="mt-4 flex flex-col items-center">
            <p className="text-sm text-gray-600 mb-2 text-center">
              Scan this QR code to open the mobile search tool on your phone.
            </p>
            <QRCodeSVG value={qrUrl} size={150} />
            <p className="text-xs text-gray-400 mt-2 break-all text-center">{qrUrl}</p>
          </div>
        )}

        {/* Divider and mode/settings buttons */}
        <div className="border-t my-4" />
        <div className="flex flex-col gap-2">
          <button
            onClick={() => (typeof setMode === 'function' && setMode(mode === 'workOrder' ? 'partSearch' : 'workOrder'))}
            className="w-full px-4 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700 font-semibold transition-all duration-150"
          >
            {mode === 'workOrder' ? 'Switch to Part Search Mode' : 'Switch to Work Order Mode'}
          </button>
          <button
            onClick={() => (typeof setIsSettingsOpen === 'function' && setIsSettingsOpen((v) => !v))}
            className="w-full px-4 py-2 rounded-lg text-white bg-gray-600 hover:bg-gray-700 font-semibold transition-all duration-150"
          >
            {isSettingsOpen ? 'Close Settings' : 'Open Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartSearchView; 